import React, { useState } from 'react';
import api from '../../lib/api';
import { format } from 'date-fns';
import { X, Receipt, Percent, BadgeDollarSign, RefreshCw, Sparkles, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';

const categories = ['advertising_marketing', 'bank_fees', 'business_meals', 'car_business_use', 'company_car', 'client_gifts', 'continuing_education', 'credit_collection_fees', 'charitable_contributions', 'professional_dues', 'depreciation', 'employee_benefits', 'employee_loans', 'foreign_earned_income', 'insurance', 'raw_materials', 'mortgage_interest', 'licenses_permits', 'maintenance_repairs', 'office_supplies', 'phone_use', 'utilities', 'shipping', 'rent', 'travel', 'software', 'personal_property_use', 'entertainment', 'recruitment_hiring', 'ai_tools_subscriptions', 'other'];
const paymentMethods = ['cash', 'credit_card', 'debit_card', 'eftpos', 'paypal', 'afterpay', 'bank_transfer', 'other'];
const taxCategories = ['work_expenses', 'home_office', 'vehicle', 'uniform', 'tools', 'education', 'donations', 'medical', 'investment', 'none'];

export default function ReceiptForm({ receipt, onSubmit, onClose }) {
  const [formData, setFormData] = useState({
    store_name: '', abn: '', invoice_number: '', amount: '', date: format(new Date(), 'yyyy-MM-dd'),
    payment_method: 'credit_card', category: 'other', gst_type: '', gst_amount: 0,
    tax_deductible: false, tax_split_percentage: 100, tax_category: 'none', notes: '', tags: [],
    is_business_expense: false, client_project: '', is_recurring: false, recurring_frequency: 'monthly',
    recurring_end_date: '', is_recurring_template: false, image_url: '', folder: '',
    ...receipt
  });
  const [tagInput, setTagInput] = useState('');
  const [categorizing, setCategorizing] = useState(false);

  const autoCategorize = async () => {
    if (!formData.store_name.trim()) return;
    setCategorizing(true);
    try {
      const { data } = await api.post('/api/ai/categorize', { store_name: formData.store_name, amount: formData.amount || '0' });
      if (data?.category) {
        const amt = parseFloat(formData.amount) || 0;
        const gst = data.gst_type || 'gst_10';
        setFormData(prev => ({ ...prev, category: data.category, gst_type: gst, gst_amount: gst === 'gst_10' ? parseFloat((amt / 11).toFixed(2)) : 0 }));
      }
    } catch (e) { console.error('Auto-categorize error:', e); }
    setCategorizing(false);
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'gst_type' || field === 'amount') {
        const amt = parseFloat(field === 'amount' ? value : prev.amount) || 0;
        const type = field === 'gst_type' ? value : prev.gst_type;
        updated.gst_amount = type === 'gst_10' ? parseFloat((amt / 11).toFixed(2)) : 0;
      }
      return updated;
    });
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({ ...prev, tags: [...(prev.tags || []), tagInput.trim()] }));
      setTagInput('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      amount: parseFloat(formData.amount),
      folder: format(new Date(formData.date), 'MMMM yyyy'),
      is_recurring_template: formData.is_recurring && !formData.recurring_parent_id ? true : (formData.is_recurring_template || false),
    });
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="receipt-form-modal">
      <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-xl"><Receipt className="w-5 h-5 text-emerald-600" /></div>
            <h2 className="text-lg font-semibold text-slate-900">{receipt?.id ? 'Edit Receipt' : 'Add Receipt'}</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="w-5 h-5" /></Button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Store Name *</Label>
              <Input value={formData.store_name} onChange={(e) => handleChange('store_name', e.target.value)} placeholder="e.g., Woolworths" required data-testid="receipt-store-name" />
            </div>
            <div className="space-y-2">
              <Label>ABN</Label>
              <Input value={formData.abn} onChange={(e) => handleChange('abn', e.target.value)} placeholder="XX XXX XXX XXX" />
            </div>
            <div className="space-y-2">
              <Label>Amount (AUD) *</Label>
              <Input type="number" step="0.01" value={formData.amount} onChange={(e) => handleChange('amount', e.target.value)} placeholder="0.00" required data-testid="receipt-amount" />
            </div>
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input type="date" value={formData.date} onChange={(e) => handleChange('date', e.target.value)} required data-testid="receipt-date" />
            </div>
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select value={formData.payment_method} onValueChange={(v) => handleChange('payment_method', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{paymentMethods.map(m => <SelectItem key={m} value={m} className="capitalize">{m.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Category</Label>
                <button type="button" onClick={autoCategorize} disabled={!formData.store_name.trim() || categorizing} className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-violet-100 text-violet-700 hover:bg-violet-200 disabled:opacity-40 transition-colors font-medium" data-testid="auto-categorize-button">
                  {categorizing ? <><Loader2 className="w-3 h-3 animate-spin" /> Predicting...</> : <><Sparkles className="w-3 h-3" /> Auto-detect</>}
                </button>
              </div>
              <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
                <SelectTrigger data-testid="receipt-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map(cat => <SelectItem key={cat} value={cat} className="capitalize">{cat.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {/* GST Section */}
          <div className="mt-6 p-4 bg-emerald-50 rounded-xl space-y-4">
            <div className="flex items-center gap-2"><BadgeDollarSign className="w-4 h-4 text-emerald-600" /><Label className="font-medium text-emerald-800">GST Classification</Label></div>
            <div className="grid grid-cols-2 gap-3">
              <button type="button" onClick={() => handleChange('gst_type', 'gst_10')} className={`p-3 rounded-xl border-2 text-left transition-all ${formData.gst_type === 'gst_10' ? 'border-emerald-500 bg-emerald-100' : 'border-slate-200 bg-white'}`} data-testid="gst-10-button">
                <p className="font-semibold text-sm text-slate-900">GST 10%</p>
                <p className="text-xs text-slate-500 mt-0.5">Taxable supply</p>
                {formData.gst_type === 'gst_10' && formData.gst_amount > 0 && <p className="text-xs text-emerald-600 font-medium mt-1">GST: ${formData.gst_amount.toFixed(2)}</p>}
              </button>
              <button type="button" onClick={() => handleChange('gst_type', 'gst_free')} className={`p-3 rounded-xl border-2 text-left transition-all ${formData.gst_type === 'gst_free' ? 'border-teal-500 bg-teal-50' : 'border-slate-200 bg-white'}`} data-testid="gst-free-button">
                <p className="font-semibold text-sm text-slate-900">GST Free</p>
                <p className="text-xs text-slate-500 mt-0.5">Input-taxed / exempt</p>
              </button>
            </div>
          </div>
          {/* Tax Section */}
          <div className="mt-6 p-4 bg-slate-50 rounded-xl space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Percent className="w-4 h-4 text-emerald-500" /><Label className="font-medium">Tax Deductible</Label></div>
              <Switch checked={formData.tax_deductible} onCheckedChange={(v) => handleChange('tax_deductible', v)} />
            </div>
            {formData.tax_deductible && (
              <>
                <div className="space-y-2">
                  <Label>Tax Category</Label>
                  <Select value={formData.tax_category} onValueChange={(v) => handleChange('tax_category', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{taxCategories.map(cat => <SelectItem key={cat} value={cat} className="capitalize">{cat.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between"><Label>Deductible Percentage</Label><span className="text-sm font-medium text-emerald-600">{formData.tax_split_percentage}%</span></div>
                  <Slider value={[formData.tax_split_percentage]} onValueChange={([v]) => handleChange('tax_split_percentage', v)} max={100} step={5} className="py-2" />
                </div>
              </>
            )}
          </div>
          {/* Business & Recurring */}
          <div className="mt-4 p-4 bg-slate-50 rounded-xl space-y-4">
            <div className="flex items-center justify-between"><Label className="font-medium">Business Expense</Label><Switch checked={formData.is_business_expense} onCheckedChange={(v) => handleChange('is_business_expense', v)} /></div>
            {formData.is_business_expense && <div className="space-y-2"><Label>Client / Project</Label><Input value={formData.client_project} onChange={(e) => handleChange('client_project', e.target.value)} placeholder="Project or client name" /></div>}
          </div>
          {/* Tags */}
          <div className="mt-4 space-y-2">
            <Label>Tags</Label>
            <div className="flex gap-2">
              <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="Add a tag" onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())} />
              <Button type="button" variant="outline" onClick={handleAddTag}>Add</Button>
            </div>
            {formData.tags?.length > 0 && <div className="flex flex-wrap gap-2 mt-2">{formData.tags.map(tag => <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm cursor-pointer hover:bg-emerald-200" onClick={() => setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }))}>{tag} <X className="w-3 h-3" /></span>)}</div>}
          </div>
          {/* Recurring */}
          <div className="mt-4 p-4 bg-violet-50 rounded-xl space-y-4">
            <div className="flex items-center justify-between"><div className="flex items-center gap-2"><RefreshCw className="w-4 h-4 text-violet-500" /><Label className="font-medium text-violet-800">Recurring Expense</Label></div><Switch checked={formData.is_recurring} onCheckedChange={(v) => handleChange('is_recurring', v)} /></div>
            {formData.is_recurring && (
              <>
                <div className="space-y-2">
                  <Label>Frequency</Label>
                  <Select value={formData.recurring_frequency} onValueChange={(v) => handleChange('recurring_frequency', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="fortnightly">Fortnightly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="annually">Annually</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>End Date (optional)</Label><Input type="date" value={formData.recurring_end_date} onChange={(e) => handleChange('recurring_end_date', e.target.value)} /></div>
              </>
            )}
          </div>
          {/* Notes */}
          <div className="mt-4 space-y-2"><Label>Notes</Label><Textarea value={formData.notes} onChange={(e) => handleChange('notes', e.target.value)} placeholder="Additional notes..." rows={3} /></div>
          <div className="flex gap-3 mt-6 pt-4 border-t border-slate-100">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600" data-testid="save-receipt-button">{receipt?.id ? 'Update' : 'Save'} Receipt</Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
